/**
 * Reading pixels back out of the game canvas is only reliable in one place: synchronously,
 * inside the animation-frame callback, immediately after `renderer.render()`. A WebGPU
 * canvas (and the WebGL2 fallback) has no `preserveDrawingBuffer` guarantee, so a
 * `toDataURL()` called from a DOM event handler or after an `await` usually comes back
 * transparent black.
 *
 * So every pixel read in the client goes through this one primitive: callers ask for a
 * frame and get a promise; the frame loop drains the queue at the end of the frame.
 */

export interface CaptureOptions {
  /** Longest edge of the returned image, in CSS pixels. */
  maxWidth: number;
  type: 'image/jpeg' | 'image/png';
  /** JPEG quality, 0..1. */
  quality: number;
}

const DEFAULTS: CaptureOptions = { maxWidth: 1280, type: 'image/jpeg', quality: 0.7 };

interface PendingCapture {
  opts: CaptureOptions;
  resolve: (dataUrl: string | null) => void;
}

/** An all-transparent PNG data URL is ~100 bytes; anything shorter than this is a failure. */
const MIN_PLAUSIBLE_LEN = 512;

export class FrameCapture {
  private readonly canvas: HTMLCanvasElement;
  private readonly queue: PendingCapture[] = [];
  private scratch: HTMLCanvasElement | null = null;
  /** Set once a capture actually produced pixels; used to report the fallback. */
  private everSucceeded = false;
  private everTried = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  /** True once at least one capture came back with real pixels. */
  get working(): boolean {
    return this.everSucceeded || !this.everTried;
  }

  /**
   * Queues a capture of the next rendered frame. Resolves with a data URL, or null if the
   * backend refused to hand over pixels (tainted canvas, empty drawing buffer, no 2D ctx).
   */
  request(opts: Partial<CaptureOptions> = {}): Promise<string | null> {
    return new Promise((resolve) => {
      this.queue.push({ opts: { ...DEFAULTS, ...opts }, resolve });
    });
  }

  get pending(): boolean {
    return this.queue.length > 0;
  }

  /**
   * Called by the frame loop directly after the render passes, still inside the same
   * animation-frame callback. Synchronous on purpose.
   */
  drain(): void {
    if (this.queue.length === 0) return;
    const jobs = this.queue.splice(0, this.queue.length);
    for (const job of jobs) {
      let url: string | null = null;
      try {
        url = this.grab(job.opts);
      } catch (err) {
        console.warn('[stillsun] frame capture failed', err);
      }
      this.everTried = true;
      if (url !== null && url.length >= MIN_PLAUSIBLE_LEN) this.everSucceeded = true;
      else url = null;
      job.resolve(url);
    }
  }

  private grab(opts: CaptureOptions): string | null {
    const src = this.canvas;
    const sw = src.width;
    const sh = src.height;
    if (sw === 0 || sh === 0) return null;

    const scale = Math.min(1, opts.maxWidth / sw);
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));

    // A full-size read followed by a downscale is the only order that works: the source
    // canvas must be sampled now, in this frame, and drawImage does the resize for free.
    this.scratch ??= document.createElement('canvas');
    const dst = this.scratch;
    dst.width = dw;
    dst.height = dh;
    const ctx = dst.getContext('2d');
    if (!ctx) return null;
    // JPEG has no alpha; without a ground the transparent HUD-less border reads as black
    // anyway, but an explicit fill keeps PNG captures from looking like a hole.
    ctx.fillStyle = '#120d0b';
    ctx.fillRect(0, 0, dw, dh);
    ctx.drawImage(src, 0, 0, sw, sh, 0, 0, dw, dh);
    return dst.toDataURL(opts.type, opts.quality);
  }
}

/** Strips the `data:image/jpeg;base64,` prefix — the API wants raw base64. */
export function base64Of(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}
